import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type KeyCatalogueEntry, type KeyValueScalar } from '../types/key-value.types';
import { type StructuredIssue } from '../types/structured-issue.types';
import { useKeyValueCatalogue } from '../useKeyValueCatalogue/useKeyValueCatalogue';
import { useKeyValueImport, type UseKeyValueImport } from '../useKeyValueImport/useKeyValueImport';
import {
  useKeyValueRows,
  type KeyValueRowOutcome,
  type KeyValueRowView,
} from '../useKeyValueRows/useKeyValueRows';
import { useStructuredAnnouncer } from '../useStructuredAnnouncer/useStructuredAnnouncer';

import { type KeyValueEditorProps } from './KeyValueEditor.types';

/** One row, plus everything only the editor can say about it. */
export interface KeyValueRowModel extends KeyValueRowView {
  /** Caller-supplied problems for this key, already translated. */
  issues: readonly StructuredIssue[];
  /** The kit's own complaint about the row: duplicate key, not a number, … */
  message: { text: string; severity: 'error' | 'warning' } | null;
  isRemovable: boolean;
}

export type UseKeyValueEditorLogicInput = Omit<
  KeyValueEditorProps,
  'id' | 'className' | 'size' | 'label' | 'aria-describedby' | 'emptyContent'
>;

export interface UseKeyValueEditorLogic {
  rows: readonly KeyValueRowModel[];
  /** Catalogue keys not on screen yet — what the add menu offers. */
  remaining: readonly KeyCatalogueEntry[];
  hasCatalogue: boolean;
  canAddCustom: boolean;
  canAdd: boolean;
  announcement: string;
  import: UseKeyValueImport;
  /** Said after a paste lands, through the shell's one live region. */
  announceImported: (changed: number) => void;
  addRow: (key?: string) => void;
  removeRow: (rowId: string) => void;
  setKey: (rowId: string, key: string) => void;
  setText: (rowId: string, text: string) => void;
  setBoolean: (rowId: string, isOn: boolean) => void;
  adoptKind: (rowId: string) => void;
  /** Registers a row's focusable control, so focus-on-error can reach it. */
  registerControl: (rowId: string, element: HTMLElement | null) => void;
  registerAddControl: (element: HTMLElement | null) => void;
  focus: () => void;
}

export function useKeyValueEditorLogic({
  value,
  onValueChange,
  valueKind = 'text',
  catalogue: entries,
  isKeyRestricted = false,
  maxRows,
  isDisabled = false,
  isReadOnly = false,
  issues,
}: UseKeyValueEditorLogicInput): UseKeyValueEditorLogic {
  const { t } = useTranslation('uiKit');
  const announcer = useStructuredAnnouncer();
  const controls = useRef(new Map<string, HTMLElement | null>());
  const addControl = useRef<HTMLElement | null>(null);

  const catalogue = useKeyValueCatalogue({
    catalogue: entries,
    defaultValueKind: valueKind,
    isKeyRestricted,
  });

  const { rows, canAdd, addRow, removeRow, setKey, setText, setBoolean, adoptKind, applyMap } =
    useKeyValueRows({ value, onValueChange, catalogue, maxRows });

  const currentMap = useMemo(() => value ?? {}, [value]);
  const importer = useKeyValueImport({ currentMap, catalogue, onApply: applyMap });

  /**
   * The ONE refusal choke point.
   *
   * Every mutating entry passes through here, so a read-only field announces
   * exactly once and mutates nothing. Refusing in two places is what swallowed
   * the announcement in the selection family's first attempt: the row's own
   * short-circuit ate the click before the hook could speak, and the operator
   * got a control that focuses, clicks and does nothing — worse than the
   * `disabled` it replaced.
   */
  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;

    announcer.announceReadOnlyRefusal();
    return true;
  }, [announcer, isDisabled, isReadOnly]);

  const announceOutcome = useCallback(
    (outcome: KeyValueRowOutcome) => {
      if (outcome.status === 'refusedMax') {
        announcer.say(t('keyValue.maxRowsBlocked', { count: outcome.max }));
        return;
      }
      if (outcome.status === 'refusedRequired') return;
      if (outcome.status === 'removed') {
        announcer.say(t('keyValue.rowRemoved', { key: outcome.key }));
        return;
      }
      announcer.say(
        outcome.key === ''
          ? t('keyValue.rowAddedBlank')
          : t('keyValue.rowAdded', { key: outcome.key }),
      );
    },
    [announcer, t],
  );

  const models = useMemo<KeyValueRowModel[]>(
    () =>
      rows.map((row) => {
        const key = row.key.trim();
        const rowIssues = (issues ?? []).filter(
          (issue) => issue.path.length > 0 && String(issue.path[0]) === key,
        );

        return {
          ...row,
          issues: rowIssues,
          message: messageFor(row, t),
          // A required key has nothing for a refusal to explain, so the
          // affordance is not drawn at all rather than drawn and refused.
          isRemovable: !row.isRequired,
        };
      }),
    [issues, rows, t],
  );

  const presentKeys = useMemo(() => rows.map((row) => row.key.trim()), [rows]);

  const focus = useCallback(() => {
    const firstProblem = models.find(
      (row) => row.issues.length > 0 || row.message?.severity === 'error',
    );
    const target =
      controls.current.get(firstProblem?.rowId ?? '') ??
      controls.current.get(models[0]?.rowId ?? '') ??
      addControl.current;

    target?.focus();
  }, [models]);

  return {
    rows: models,
    remaining: catalogue.remainingFor(presentKeys),
    hasCatalogue: catalogue.hasCatalogue,
    canAddCustom: !isKeyRestricted,
    canAdd: canAdd && !isDisabled && !isReadOnly,
    announcement: announcer.announcement,
    import: importer,
    announceImported: useCallback(
      (changed: number) => {
        announcer.say(t('keyValue.import.applied', { count: changed }));
      },
      [announcer, t],
    ),
    addRow: useCallback(
      (key?: string) => {
        if (refuse()) return;
        announceOutcome(addRow(key));
      },
      [addRow, announceOutcome, refuse],
    ),
    removeRow: useCallback(
      (rowId: string) => {
        if (refuse()) return;
        announceOutcome(removeRow(rowId));
      },
      [announceOutcome, refuse, removeRow],
    ),
    setKey: useCallback(
      (rowId: string, key: string) => {
        if (refuse()) return;
        setKey(rowId, key);
      },
      [refuse, setKey],
    ),
    setText: useCallback(
      (rowId: string, text: string) => {
        if (refuse()) return;
        setText(rowId, text);
      },
      [refuse, setText],
    ),
    setBoolean: useCallback(
      (rowId: string, isOn: boolean) => {
        if (refuse()) return;
        setBoolean(rowId, isOn);
      },
      [refuse, setBoolean],
    ),
    adoptKind: useCallback(
      (rowId: string) => {
        if (refuse()) return;
        adoptKind(rowId);
      },
      [adoptKind, refuse],
    ),
    registerControl: useCallback((rowId: string, element: HTMLElement | null) => {
      if (element === null) controls.current.delete(rowId);
      else controls.current.set(rowId, element);
    }, []),
    registerAddControl: useCallback((element: HTMLElement | null) => {
      addControl.current = element;
    }, []),
    focus,
  };
}

/** The kit's own complaint about a row, in priority order. */
function messageFor(
  row: KeyValueRowView,
  t: (key: string, options?: Record<string, unknown>) => string,
): KeyValueRowModel['message'] {
  if (row.isDuplicate) return { text: t('keyValue.duplicateKey'), severity: 'error' };
  if (row.isNotANumber) return { text: t('keyValue.notANumber'), severity: 'error' };
  if (row.isMismatched) {
    return {
      text: t('keyValue.mismatchedValue', {
        actual: t(`keyValue.kind.${actualKindOf(row.original)}`),
        expected: t(`keyValue.kind.${row.kind}`),
      }),
      severity: 'warning',
    };
  }
  if (row.isOutOfRange) {
    return {
      text: t('keyValue.outOfRange', { min: row.entry?.min ?? '', max: row.entry?.max ?? '' }),
      severity: 'warning',
    };
  }

  return null;
}

function actualKindOf(value: KeyValueScalar | undefined): 'text' | 'number' | 'boolean' {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'text';
}
